import { Injectable, Inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { TokenService } from './token.service';
import { SafeStorage } from '../utils/storage.utils';

/**
 * 🔐 Servicio de AUTENTICACIÓN (Solo login/logout/tokens)
 * NO maneja perfil de usuario - eso es responsabilidad de UserService
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl;

  requiresUsername = signal<boolean>(false);
  emailVerified = signal<boolean>(true);

  private readonly REQUIRES_USERNAME_KEY = 'requires_username';
  private readonly EMAIL_VERIFIED_KEY = 'email_verified';

  // Estado simple: ¿está autenticado?
  isLoggedIn = signal<boolean>(false);

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private tokenService: TokenService,
    private router: Router,
  ) {
    // Solo verificar si hay token al iniciar
    this.checkInitialAuth();
  }

  /**
   * Verifica autenticación inicial (solo token)
   */
  private checkInitialAuth(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const hasValidToken = this.tokenService.isTokenValid();
    this.isLoggedIn.set(hasValidToken);

    if (hasValidToken) {
      // 1. Username
      const pendingUsername =
        SafeStorage.getItem(this.REQUIRES_USERNAME_KEY) === 'true';
      this.requiresUsername.set(pendingUsername);

      // 2. Email Verified (Mejorado)
      const storedVerified = SafeStorage.getItem(this.EMAIL_VERIFIED_KEY);
      // Si explícitamente es 'true', ponemos true. Si es 'false', false.
      this.emailVerified.set(storedVerified !== 'false');
    }
  }

  get token(): string | null {
    return this.tokenService.token;
  }

  /**
   * Verifica si el usuario está autenticado
   */
  checkAuth(): boolean {
    return this.isLoggedIn() && this.tokenService.isTokenValid();
  }

  /**
   * Login con email y password
   * Retorna el token, NO el perfil
   */
  login(email: string, password: string): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/login`, { email, password })
      .pipe(
        tap((response) => {
          if (response.token) {
            this.setToken(response.token);
            this.emailVerified.set(response.email_verified ?? true); // ← Agregar
          }
        }),
        catchError((error) => {
          this.clearAuth();
          return throwError(() => error);
        }),
      );
  }

  /**
   * Autenticación con Google
   */
  googleAuth(token: string, mode?: 'register' | 'login'): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/auth/google`, { token, mode })
      .pipe(
        tap((response) => {
          if (response.token) {
            const needsUsername = response.requires_username ?? false;

            // ✅ Persistir y setear ANTES de setToken — así cualquier efecto
            // que se dispare al cambiar isLoggedIn ya ve el signal correcto
            if (needsUsername) {
              SafeStorage.setItem(this.REQUIRES_USERNAME_KEY, 'true');
            } else {
              SafeStorage.removeItem(this.REQUIRES_USERNAME_KEY);
            }
            this.requiresUsername.set(needsUsername);
            this.emailVerified.set(response.email_verified ?? true);
            this.setToken(response.token);
          }
        }),
        catchError((error) => {
          this.clearAuth();
          return throwError(() => error);
        }),
      );
  }

  /**
   * Cierra sesión del usuario
   */
  logout(): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/logout`, {}).pipe(
      tap(() => {
        this.clearAuth();
        this.router.navigate(['/login']);
      }),
      catchError((error) => {
        console.error('Error en logout:', error);
        this.clearAuth();
        this.router.navigate(['/login']);
        // No lanzar error, permitir que se limpie localmente
        return of({ status: true, message: 'Sesión cerrada localmente' });
      }),
    );
  }

  /**
   * Establece el token de autenticación
   */
  setToken(token: string, source?: string): void {
    this.tokenService.setToken(token);
    this.isLoggedIn.set(true);
  }

  /**
   * Limpia la autenticación y todo rastro de la sesión en el navegador
   */
  clearAuth(): void {
    if (isPlatformBrowser(this.platformId)) {
      // 1. Limpiar todo el localStorage y sessionStorage, preservando temas, idioma y cookies
      SafeStorage.clear(['theme', 'user_lang', 'myplace_cookie_consent']);
      SafeStorage.clearSession();

      // 2. Limpiar Cachés del navegador (Cache Storage API)
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }
    }

    // 3. Resetear estados internos (Signals)
    this.tokenService.removeToken();
    this.isLoggedIn.set(false);
    this.requiresUsername.set(false);
    this.emailVerified.set(true);
  }

  /**
   * Envía el link de recuperación al email
   */
  forgotPassword(email: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/forgot-password`, { email });
  }

  /**
   * Restablece la contraseña usando el token
   */
  resetPassword(data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reset-password`, data);
  }
}
